import Select from "react-select";
import { useIntl } from "react-intl";
import { colors } from "../../lib/constants";

const { backgroundCustomPink, greyBlue, purple } = colors;

export interface Language {
  code: string;
  en: string;
  ja: string;
  fr: string;
  vi: string;
  native?: string;
  // the translated voice can be played in this language
  voiceRestitution?: boolean;
  [key: string]: string | boolean | undefined;
}

// The language lists only ship labels for a handful of locales, so for the
// other UI languages we ask the browser for the name of the language.
const displayNamesByLocale = new Map<string, Intl.DisplayNames | null>();

const getDisplayNames = (locale: string) => {
  if (!displayNamesByLocale.has(locale)) {
    let displayNames: Intl.DisplayNames | null = null;
    try {
      displayNames = new Intl.DisplayNames([locale], { type: "language" });
    } catch (error) {
      displayNames = null;
    }
    displayNamesByLocale.set(locale, displayNames);
  }
  return displayNamesByLocale.get(locale) ?? null;
};

export const getLanguageLabel = (lang: Language, locale: string) => {
  const label = lang[locale];
  if (typeof label === "string" && label) {
    return label;
  }
  let displayName;
  try {
    displayName = getDisplayNames(locale)?.of(lang.code);
  } catch (error) {
    displayName = undefined;
  }
  // Intl returns the code itself for a language it doesn't know
  if (displayName && displayName !== lang.code) {
    return displayName;
  }
  return lang.native || lang.en;
};

// Not every UI language can be transcribed, and some are only offered as a
// regional variant (pt is pt-pt/pt-br), so map the locale onto a code the
// transcription engine actually knows about.
export const toSupportedLanguage = (
  language: string,
  languages: Language[],
  fallback = "en"
) => {
  // The available languages are fetched asynchronously, don't reset the
  // language of the user while we are still waiting for them.
  if (languages.length === 0) {
    return language;
  }
  const codes = languages.map((lang) => lang.code);
  if (codes.includes(language)) {
    return language;
  }
  const regional = codes.filter((code) => code.startsWith(`${language}-`));
  if (regional.length > 0) {
    return (
      regional.find((code) => code === `${language}-${language}`) ?? regional[0]
    );
  }
  return codes.includes(fallback) ? fallback : codes[0];
};

interface LanguageSelectProps {
  id: string;
  languages: Language[];
  value: string;
  onChange: (code: string) => void;
  // above by default, the selects of the meeting are at the bottom of the screen
  menuPlacement?: "top" | "bottom" | "auto";
}

const normalizeString = (str: string) => {
  return str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
};

const filterOption = (option: any, inputValue: string) => {
  if (!inputValue) return true;
  const label = option.label || "";
  const normalizedLabel = normalizeString(label);
  const normalizedInput = normalizeString(inputValue);
  return normalizedLabel.startsWith(normalizedInput);
};

export const LanguageSelect = ({
  id,
  languages,
  value,
  onChange,
  menuPlacement = "top",
}: LanguageSelectProps) => {
  const intl = useIntl();
  const locale = intl.locale;

  const getLabel = (lang: Language) => getLanguageLabel(lang, locale);

  return (
    <Select
      id={id}
      isSearchable
      menuPlacement={menuPlacement}
      filterOption={filterOption}
      onChange={(selectedOption) => {
        if (selectedOption) {
          onChange(selectedOption.value);
        }
      }}
      value={languages
        .map((lang) => ({
          value: lang.code,
          label: getLabel(lang),
        }))
        .find((option) => option.value === value)}
      options={[...languages]
        .sort((a, b) => {
          const labelA = getLabel(a);
          const labelB = getLabel(b);
          return labelA.localeCompare(labelB, locale);
        })
        .map((lang) => ({
          value: lang.code,
          label: getLabel(lang),
        }))}
      // @ts-ignore
      theme={(theme) => ({
        ...theme,
        colors: {
          ...theme.colors,
          primary25: backgroundCustomPink,
          primary50: backgroundCustomPink,
          primary: purple,
        },
      })}
      styles={{
        // @ts-ignore
        control: (baseStyles, state) => ({
          ...baseStyles,
          minHeight: "38px",
          height: "38px",
          "&:hover": {
            borderColor: state.isFocused ? purple : greyBlue,
          },
        }),
      }}
    />
  );
};
